import type {
  GameContext,
  GameEvent,
  GameInstance,
  GameManifest,
  PauseReason,
  ViewportSnapshot,
} from "../contracts/game";

export interface GameScope {
  readonly context: GameContext;
  abort(): void;
  dispose(): void;
}

export interface GameScopeFactory {
  create(
    gameId: string,
    host: HTMLElement,
    emit: (event: GameEvent) => void,
    requestHome: () => void,
  ): GameScope;
}

export interface GameHostHooks {
  onRunning?(gameId: string): void;
  onEvent?(gameId: string, event: GameEvent): void;
  onRequestHome?(gameId: string): void;
  onError?(gameId: string, error: Error): void;
}

interface ActiveGame {
  generation: number;
  gameId: string;
  host: HTMLElement;
  scope: GameScope;
  instance: GameInstance | null;
  cleaned: boolean;
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

export class GameHost {
  private generation = 0;
  private active: ActiveGame | null = null;

  constructor(
    private readonly scopeFactory: GameScopeFactory,
    private readonly hooks: GameHostHooks = {},
  ) {}

  async open(manifest: GameManifest, host: HTMLElement): Promise<boolean> {
    if (this.active) await this.close();
    const generation = ++this.generation;
    const isCurrent = (): boolean => this.active?.generation === generation;
    const scope = this.scopeFactory.create(
      manifest.id,
      host,
      (event) => {
        if (isCurrent()) this.hooks.onEvent?.(manifest.id, event);
      },
      () => {
        if (isCurrent()) this.hooks.onRequestHome?.(manifest.id);
      },
    );
    const active: ActiveGame = {
      generation,
      gameId: manifest.id,
      host,
      scope,
      instance: null,
      cleaned: false,
    };
    this.active = active;

    try {
      const module = await manifest.load();
      if (!isCurrent()) return false;

      const instance = module.create(scope.context);
      active.instance = instance;
      await instance.mount(host);
      if (!isCurrent()) return false;

      await instance.start();
      if (!isCurrent()) return false;

      this.hooks.onRunning?.(manifest.id);
      return true;
    } catch (value) {
      if (!isCurrent()) return false;
      const error = asError(value);
      this.active = null;
      await this.cleanup(active);
      this.hooks.onError?.(manifest.id, error);
      return false;
    }
  }

  async close(): Promise<void> {
    ++this.generation;
    const active = this.active;
    this.active = null;
    if (active) await this.cleanup(active);
  }

  pause(reason: PauseReason): void {
    const active = this.active;
    if (!active?.instance) return;
    try {
      active.scope.context.clock.reset();
      active.instance.pause(reason);
    } catch (value) {
      void this.failActive(active, asError(value));
    }
  }

  resume(): void {
    const active = this.active;
    if (!active?.instance) return;
    try {
      active.scope.context.clock.reset();
      active.instance.resume();
    } catch (value) {
      void this.failActive(active, asError(value));
    }
  }

  resize(viewport: ViewportSnapshot): void {
    const active = this.active;
    if (!active?.instance) return;
    try {
      active.instance.resize(viewport);
    } catch (value) {
      void this.failActive(active, asError(value));
    }
  }

  get currentInstance(): GameInstance | null {
    return this.active?.instance ?? null;
  }

  private async failActive(active: ActiveGame, error: Error): Promise<void> {
    if (this.active !== active) return;
    ++this.generation;
    this.active = null;
    await this.cleanup(active);
    this.hooks.onError?.(active.gameId, error);
  }

  private async cleanup(active: ActiveGame): Promise<void> {
    if (active.cleaned) return;
    active.cleaned = true;
    active.scope.abort();
    try {
      await active.instance?.destroy();
    } catch (value) {
      this.hooks.onError?.(active.gameId, asError(value));
    }
    active.scope.dispose();
    active.host.replaceChildren();
  }
}
